import {
	ExclamationTriangleIcon,
	FlagIcon,
	MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { IncidentOutcomeDisplayNames } from "@referee-fyi/share";
import { useCallback, useMemo, useState } from "react";
import type { EventData, TeamData } from "robotevents";
import { Button } from "~components/Button";
import { DisconnectedWarning } from "~components/DisconnectedWarning";
import { EventNewIncidentDialog } from "~components/dialogs/new";
import { IconLabel, Input } from "~components/Input";
import { InspectionStatusDot } from "~components/Inspection";
import { MenuButton } from "~components/MenuButton";
import { Spinner } from "~components/Spinner";
import { TeamMenu } from "~components/TeamMenu";
import { VirtualizedList } from "~components/VirtualizedList";
import type { Incident, RichIncident } from "~utils/data/incident";
import { useNewIncidentDialogState } from "~utils/dialogs/new";
import { filterTeams } from "~utils/filterteams";
import {
	useEventIncidents,
	useTeamInspectionStatus,
} from "~utils/hooks/incident";
import { useDivisionTeams } from "~utils/hooks/robotevents";
import { useCurrentDivision } from "~utils/hooks/state";

export type TeamListItemProps = {
	team: TeamData;
	sku: string;
	incidents: Incident[];
	majorCount: number;
	minorCount: number;
	onNewIncident: (initial: Partial<RichIncident>) => void;
};

export const TeamListItemMenu: React.FC<TeamListItemProps> = ({
	team,
	sku,
	incidents,
	onNewIncident,
}) => {
	return (
		<TeamMenu team={team} sku={sku} incidents={incidents}>
			<Button
				mode="normal"
				align="left"
				className="mt-4 bg-emerald-600"
				onClick={() =>
					onNewIncident({
						team: team.number,
						outcome: "InspectionPassed",
					})
				}
			>
				<FlagIcon height={20} className="inline mr-2 " />
				{IncidentOutcomeDisplayNames.InspectionPassed}
			</Button>
			<Button
				mode="normal"
				align="left"
				className="mt-4 bg-red-500"
				onClick={() =>
					onNewIncident({
						team: team.number,
						outcome: "InspectionFailed",
					})
				}
			>
				<FlagIcon height={20} className="inline mr-2 " />
				{IncidentOutcomeDisplayNames.InspectionFailed}
			</Button>
			<hr className="mt-4 border-zinc-600" />
			<Button
				mode="normal"
				align="left"
				className="mt-4 bg-blue-600"
				onClick={() =>
					onNewIncident({
						team: team.number,
						outcome: "General",
					})
				}
			>
				<FlagIcon height={20} className="inline mr-2" />
				General
			</Button>
			<Button
				mode="normal"
				align="left"
				className="mt-4 bg-yellow-500"
				onClick={() =>
					onNewIncident({
						team: team.number,
						outcome: "Minor",
					})
				}
			>
				<FlagIcon height={20} className="inline mr-2 " />
				Minor
			</Button>
			<Button
				mode="normal"
				align="left"
				className="mt-4 bg-red-500"
				onClick={() =>
					onNewIncident({
						team: team.number,
						outcome: "Major",
					})
				}
			>
				<FlagIcon height={20} className="inline mr-2 " />
				Major
			</Button>
		</TeamMenu>
	);
};

export const TeamListItem: React.FC<TeamListItemProps> = ({
	team,
	sku,
	incidents,
	majorCount,
	minorCount,
	onNewIncident,
}) => {
	const inspectionStatus = useTeamInspectionStatus(team.number, sku);

	return (
		<MenuButton
			mode="transparent"
			menu={
				<TeamListItemMenu
					team={team}
					sku={sku}
					incidents={incidents}
					majorCount={majorCount}
					minorCount={minorCount}
					onNewIncident={onNewIncident}
				/>
			}
			className="flex items-center gap-4 mt-4 p-0 h-12 text-zinc-50 active:bg-zinc-600"
			aria-label={`Team ${team.number} ${team.team_name}. ${majorCount} major violations. ${minorCount} minor violations`}
		>
			<div className="flex-1">
				<p className="text-emerald-400 font-mono flex items-center gap-1.5">
					{team.number}
					<InspectionStatusDot status={inspectionStatus.status} />
				</p>
				<p className="overflow-hidden whitespace-nowrap text-ellipsis max-w-[24ch] lg:max-w-prose">
					{team.team_name}
				</p>
			</div>
			<div className="absolute right-0 bg-zinc-800 h-full w-32 px-2 flex items-center justify-between">
				<span className="text-red-400 mr-4">
					<FlagIcon height={20} className="inline" />
					<span className="font-mono ml-2">{majorCount}</span>
				</span>
				<span className="text-yellow-400">
					<ExclamationTriangleIcon height={20} className="inline" />
					<span className="font-mono ml-2">{minorCount}</span>
				</span>
			</div>
		</MenuButton>
	);
};

export type EventTagProps = {
	event: EventData;
};

export const EventTeamsTab: React.FC<EventTagProps> = ({ event }) => {
	const division = useCurrentDivision();
	const {
		data: divisionTeams,
		isLoading,
		isPaused,
	} = useDivisionTeams(event, division);
	const { data: incidents } = useEventIncidents(event.sku);
	const [filter, setFilter] = useState("");

	const newIncidentDialog = useNewIncidentDialogState();
	const openNewIncidentDialog = useCallback(
		(initial: Partial<RichIncident> = {}) => {
			newIncidentDialog.setOpen({
				open: true,
				initial: {
					...initial,
				},
			});
		},
		[newIncidentDialog],
	);

	const teams = useMemo(() => divisionTeams?.teams ?? [], [divisionTeams]);

	const majorIncidents = useMemo(() => {
		if (!incidents) return new Map<string, number>();

		const grouped = new Map<string, number>();

		for (const incident of incidents) {
			if (incident.outcome !== "Major") continue;
			const key = incident.team ?? "<none>";
			const count = grouped.get(key) ?? 0;
			grouped.set(key, count + 1);
		}

		return grouped;
	}, [incidents]);

	const minorIncidents = useMemo(() => {
		if (!incidents) return new Map<string, number>();

		const grouped = new Map<string, number>();

		for (const incident of incidents) {
			if (
				incident.outcome !== "Minor" &&
				incident.outcome !== "Disabled" &&
				incident.outcome !== "General"
			)
				continue;
			const key = incident.team ?? "<none>";
			const count = grouped.get(key) ?? 0;
			grouped.set(key, count + 1);
		}

		return grouped;
	}, [incidents]);

	const filteredTeams = useMemo(
		() => (teams ? filterTeams(teams, filter) : []),
		[filter, teams],
	);

	return (
		<section className="contents">
			<DisconnectedWarning />
			<IconLabel icon={<MagnifyingGlassIcon height={24} />}>
				<Input
					placeholder="Search teams..."
					className="flex-1"
					value={filter}
					onChange={(e) => setFilter(e.currentTarget.value.toUpperCase())}
				/>
			</IconLabel>
			<Spinner show={isLoading || isPaused} />
			<EventNewIncidentDialog state={newIncidentDialog} />
			<VirtualizedList
				data={filteredTeams}
				options={{ estimateSize: () => 64 }}
				className="flex-1"
				parts={{ list: { className: "mb-12" } }}
			>
				{(team) => (
					<TeamListItem
						team={team}
						sku={event.sku}
						incidents={incidents ?? []}
						majorCount={majorIncidents.get(team.number) ?? 0}
						minorCount={minorIncidents.get(team.number) ?? 0}
						onNewIncident={openNewIncidentDialog}
					/>
				)}
			</VirtualizedList>
		</section>
	);
};
